import { TEMP_ID_PREFIX } from "common";
import { uniqueId } from "lodash";
import {
    DisplayStepOption,
    Field,
    FieldPayload,
    FieldType,
    FieldVisibility,
    FieldWithPickupOptions,
    Form,
    FormSource,
    Product,
    ProductObject,
    Seller,
} from "types";

export const createNewForm = (
    seller: Seller,
    organizerOnly: boolean,
    product?: Product,
    object?: ProductObject,
): Form => {
    const form: Form = {
        fields: [],
        organizerOnly,
        seller: { id: seller.id },
        productType: object ?? ProductObject.EXPERIENCE, // Global Questionnaire defaults to EXPERIENCE
    };
    if (product) {
        form.product = { id: product.id };
    }

    return form;
};

export const getFormPayload = (form: Form) => {
    return {
        ...form,
        fields: form.fields.map((innerField) => {
            const field: FieldPayload = { ...innerField };
            if (field.id?.startsWith(TEMP_ID_PREFIX)) {
                delete field.id;
            }
            if (field.catalogItem?.id) {
                field.catalogItem = { id: field.catalogItem.id };
            }
            if (isPickupLocationField(field)) {
                (field as FieldWithPickupOptions).options = (field as FieldWithPickupOptions).options.map((option) => {
                    const optionPayload = { ...option };
                    if (optionPayload.pickupLocation?.id) {
                        optionPayload.pickupLocation = { id: optionPayload.pickupLocation.id };
                    }
                    return optionPayload;
                });
            }

            return field;
        }),
    };
};

export const isPickupLocationField = (
    field: Field | FieldWithPickupOptions | FieldPayload,
): field is FieldWithPickupOptions => {
    return field.type === FieldType.PICKUP;
};

export const isQuestionnaireSkipped = (form?: Form | null) => !!form?.optionalSourcesList?.includes(FormSource.XOLA);

export const hasOrganizerOnlyQuestions = (form?: Form) => form?.fields.some((field) => field.organizerOnly);

export const getProductForm = (forms: Form[], productId?: string) => {
    return forms.find((form) => (productId ? form.product?.id === productId : !form.product));
};

export const getMarketingConsentFields = (form: Form) => {
    return form.fields.filter((field) => field.type === FieldType.MARKETING_CONSENT) ?? [];
};

/**
 * Creates the mandatory Email field for membership questionnaire forms.
 * This field is always required, shown before payment, and cannot be edited by the seller.
 */
export const createMembershipEmailField = (): Field => ({
    id: uniqueId(TEMP_ID_PREFIX),
    label: "Email",
    organizerOnly: false,
    required: true,
    roster: false,
    addToSavedViews: false,
    type: FieldType.EMAIL,
    readonly: false,
    seq: 0,
    displayStep: DisplayStepOption.BEFORE_PAYMENT,
    visibility: FieldVisibility.ALL,
});

export const ensureMembershipEmailField = (form: Form): Form => {
    if (form.fields.some((field) => field.type === FieldType.EMAIL)) {
        return form;
    }

    return { ...form, fields: [createMembershipEmailField(), ...form.fields] };
};
